import { useState } from 'react';
import { api } from '../lib/api';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Loader2, Link as LinkIcon, MessageCircle, Upload, Download, FileSpreadsheet, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentLinkGenerator() {
  const [activeTab, setActiveTab] = useState('single');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    phone: '',
    amount: '',
    description: '',
  });
  const [paymentLink, setPaymentLink] = useState(null);

  // Bulk state
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/payments/create-link', {
        customer_name: formData.customer_name,
        phone: formData.phone,
        amount: parseFloat(formData.amount),
        description: formData.description,
      });

      setPaymentLink(response.data);
      toast.success('Payment link generated successfully!');

      if (response.data.whatsapp_url) {
        window.open(response.data.whatsapp_url, '_blank');
      }

      setFormData({
        customer_name: '',
        phone: '',
        amount: '',
        description: '',
      });
    } catch (error) {
      console.error('Failed to create payment link:', error);
      toast.error(error.response?.data?.detail || 'Failed to create payment link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (paymentLink?.payment_link) {
      navigator.clipboard.writeText(paymentLink.payment_link);
      toast.success('Payment link copied to clipboard!');
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setBulkLoading(true);
    setBulkResults(null);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await api.post('/payments/create-bulk-links', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setBulkResults(response.data);
      if (response.data.successful > 0) {
        toast.success(`${response.data.successful} payment links created!`);
      }
      if (response.data.failed > 0) {
        toast.error(`${response.data.failed} rows failed`);
      }
    } catch (error) {
      console.error('Bulk upload failed:', error);
      toast.error(error.response?.data?.detail || 'Bulk upload failed');
    } finally {
      setBulkLoading(false);
      e.target.value = '';
    }
  };

  const downloadTemplate = () => {
    const csv = 'customer_name,phone,amount,description\nJohn Doe,9876543210,500,Monthly payment\nJane Smith,9876543211,1000,Service fee\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'payment_links_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  return (
    <div data-testid="payment-generator-page">
      <div className="mb-8">
        <h1 className="text-4xl font-black font-heading text-gray-900 mb-2">Payment Link Generator</h1>
        <p className="text-gray-600">Create and send payment links via WhatsApp</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit" data-testid="payment-tabs">
        <button
          onClick={() => setActiveTab('single')}
          data-testid="tab-single"
          className={`px-5 py-2.5 rounded-md text-sm font-semibold transition-all ${
            activeTab === 'single'
              ? 'bg-white text-shizah-blue shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Single Link
        </button>
        <button
          onClick={() => setActiveTab('bulk')}
          data-testid="tab-bulk"
          className={`px-5 py-2.5 rounded-md text-sm font-semibold transition-all ${
            activeTab === 'bulk'
              ? 'bg-white text-shizah-blue shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Bulk Upload
        </button>
      </div>

      {/* Single Link Tab */}
      {activeTab === 'single' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-xl font-bold font-heading text-gray-900 mb-4">Create Payment Link</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="customer_name">Customer Name *</Label>
                <Input
                  id="customer_name"
                  data-testid="input-customer-name"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  required
                  placeholder="Enter customer name"
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  data-testid="input-phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  pattern="[0-9]{10}"
                  placeholder="10-digit mobile number"
                />
              </div>

              <div>
                <Label htmlFor="amount">Amount (INR) *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  data-testid="input-amount"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                  placeholder="Enter amount in rupees"
                />
              </div>

              <div>
                <Label htmlFor="description">Description (Optional)</Label>
                <Textarea
                  id="description"
                  data-testid="input-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Payment description or purpose"
                  rows="3"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="generate-link-button"
                className="w-full bg-shizah-blue hover:bg-blue-700 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={20} />
                    Generating...
                  </>
                ) : (
                  <>
                    <LinkIcon size={20} className="mr-2" />
                    Generate Payment Link
                  </>
                )}
              </Button>
            </form>
          </div>

          {paymentLink && (
            <div className="bg-green-50 rounded-lg border border-green-200 p-6">
              <h2 className="text-xl font-bold font-heading text-green-900 mb-4">Link Generated!</h2>
              <div className="space-y-4">
                <div>
                  <Label className="text-green-900">Payment ID</Label>
                  <p className="font-mono text-sm bg-white p-2 rounded border border-green-200">
                    {paymentLink.payment_id}
                  </p>
                </div>

                <div>
                  <Label className="text-green-900">Payment Link</Label>
                  <div className="flex gap-2">
                    <Input
                      value={paymentLink.payment_link}
                      readOnly
                      className="flex-1 bg-white"
                    />
                    <Button
                      onClick={handleCopyLink}
                      variant="outline"
                      data-testid="copy-link-button"
                    >
                      Copy
                    </Button>
                  </div>
                </div>

                <div className="bg-white p-4 rounded border border-green-200">
                  <p className="text-sm text-gray-700 mb-3">
                    WhatsApp message has been opened automatically. If not, click below:
                  </p>
                  <a
                    href={paymentLink.whatsapp_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 transition-colors"
                  >
                    <MessageCircle size={20} className="mr-2" />
                    Open WhatsApp
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bulk Upload Tab */}
      {activeTab === 'bulk' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-xl font-bold font-heading text-gray-900 mb-4">Bulk Payment Link Generation</h2>
            <p className="text-gray-600 mb-6">Upload a CSV file to generate multiple payment links at once.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Upload Section */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <FileSpreadsheet size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">Upload CSV File</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Required columns: customer_name, phone, amount, description
                </p>
                <label className="cursor-pointer">
                  <Input
                    type="file"
                    accept=".csv"
                    onChange={handleBulkUpload}
                    disabled={bulkLoading}
                    className="hidden"
                    data-testid="bulk-csv-upload"
                    id="bulk-csv-upload"
                  />
                  <Button
                    type="button"
                    disabled={bulkLoading}
                    onClick={() => document.getElementById('bulk-csv-upload').click()}
                    className="bg-shizah-blue hover:bg-blue-700 text-white"
                    data-testid="bulk-upload-button"
                  >
                    {bulkLoading ? (
                      <>
                        <Loader2 className="animate-spin mr-2" size={20} />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Upload size={20} className="mr-2" />
                        Choose CSV File
                      </>
                    )}
                  </Button>
                </label>
              </div>

              {/* Template Section */}
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">CSV Template</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Download the template CSV, fill in the details, and upload.
                </p>
                <div className="bg-white rounded p-3 mb-4 font-mono text-xs overflow-x-auto">
                  <div className="text-gray-500">customer_name,phone,amount,description</div>
                  <div>John Doe,9876543210,500,Monthly payment</div>
                  <div>Jane Smith,9876543211,1000,Service fee</div>
                </div>
                <Button
                  onClick={downloadTemplate}
                  variant="outline"
                  data-testid="download-template-button"
                  className="w-full"
                >
                  <Download size={18} className="mr-2" />
                  Download Template
                </Button>
              </div>
            </div>
          </div>

          {/* Bulk Results */}
          {bulkResults && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-xl font-bold font-heading text-gray-900 mb-4">Upload Results</h2>

              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-gray-900">{bulkResults.total_rows}</p>
                  <p className="text-sm text-gray-600">Total Rows</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-700">{bulkResults.successful}</p>
                  <p className="text-sm text-green-600">Successful</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-red-700">{bulkResults.failed}</p>
                  <p className="text-sm text-red-600">Failed</p>
                </div>
              </div>

              {/* Success Table */}
              {bulkResults.results.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                    <CheckCircle size={18} /> Successfully Created
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="bulk-results-table">
                      <thead className="bg-green-50 border-b">
                        <tr>
                          <th className="px-4 py-2 text-left font-semibold">Row</th>
                          <th className="px-4 py-2 text-left font-semibold">Customer</th>
                          <th className="px-4 py-2 text-left font-semibold">Phone</th>
                          <th className="px-4 py-2 text-left font-semibold">Amount</th>
                          <th className="px-4 py-2 text-left font-semibold">Payment Link</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {bulkResults.results.map((r) => (
                          <tr key={r.payment_id} className="hover:bg-gray-50">
                            <td className="px-4 py-2">{r.row}</td>
                            <td className="px-4 py-2">{r.customer_name}</td>
                            <td className="px-4 py-2">{r.phone}</td>
                            <td className="px-4 py-2 font-semibold">&#8377;{r.amount}</td>
                            <td className="px-4 py-2">
                              <a
                                href={r.payment_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-shizah-blue hover:underline text-xs break-all"
                              >
                                {r.payment_link}
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Errors Table */}
              {bulkResults.errors.length > 0 && (
                <div>
                  <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                    <XCircle size={18} /> Failed Rows
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-red-50 border-b">
                        <tr>
                          <th className="px-4 py-2 text-left font-semibold">Row</th>
                          <th className="px-4 py-2 text-left font-semibold">Customer</th>
                          <th className="px-4 py-2 text-left font-semibold">Error</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {bulkResults.errors.map((err, idx) => (
                          <tr key={idx} className="hover:bg-red-50/50">
                            <td className="px-4 py-2">{err.row}</td>
                            <td className="px-4 py-2">{err.customer_name || '-'}</td>
                            <td className="px-4 py-2 text-red-700">{err.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
